import { createContext } from 'react';

import type { BillingLock } from '@/features/billing/api/billing';

import type { CurrentUser } from './api/auth';

/** The platform owner's role, mirroring `Role.SUPER_ADMIN` in the backend. */
export const PLATFORM_OWNER_ROLE = 'SUPER_ADMIN';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  status: AuthStatus;
  user: CurrentUser['user'] | null;
  permissions: string[];
  hasBusiness: boolean;
  /**
   * True for ShopLaunch staff, who belong to no business and work in
   * `/platform` rather than `/app`.
   *
   * Derived from the permissions the API returned rather than from the role
   * name, so the two never disagree: whoever the backend lets into the platform
   * endpoints is exactly who the client routes there.
   */
  isPlatformStaff: boolean;
  /**
   * True for affiliate partners, who also belong to no business and work in
   * `/partner`.
   *
   * Derived from the permission prefix for the same reason as the flag above,
   * and it matters more here: without it a partner signing in is sent to `/app`,
   * bounced for having no business, and asked to create a shop they never
   * wanted. A partner introduces businesses; they do not own one.
   */
  isPartner: boolean;
  /**
   * True for the platform's owner - the `SUPER_ADMIN` role - and nobody else.
   *
   * The one gate read from the role rather than the permissions, for the few
   * pages the backend keeps to that role outright instead of to a permission
   * it could hand to somebody else. Hiding them from everyone else is only
   * convenience: the API refuses them regardless.
   */
  isPlatformOwner: boolean;
  /**
   * True while this account is still using a password somebody else chose.
   *
   * The API refuses every endpoint but changing it, so the router sends these
   * callers to `/change-password` and nowhere else. Rendering the dashboard for
   * them would be a screen of failed requests.
   */
  mustChangePassword: boolean;
  /**
   * The lock on this merchant's billing, or null for everyone in good
   * standing. While it holds the API refuses every tenant endpoint but
   * billing, so the router sends the merchant to `/subscribe?locked=1` and
   * keeps only the billing page open.
   */
  billingLock: BillingLock | null;
  /** Whether the caller holds `permission`; `"A|B"` asks for either. */
  can: (permission: string) => boolean;
  /** `captchaToken` once a failed attempt has asked for a Turnstile check. */
  signIn: (email: string, password: string, captchaToken?: string) => Promise<void>;
  signOut: () => Promise<void>;
  reload: () => Promise<void>;
}

/** Kept apart from the provider so the provider file only exports components. */
export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Whether `granted` covers `permission`. `"A|B"` asks for either - a page two
 * different grants open, like the diary for the front desk (`BOOKING_READ`)
 * and for the doctor who sees only their own (`BOOKING_OWN`). No permission
 * name contains a bar.
 */
export function holdsPermission(granted: readonly string[], permission: string): boolean {
  return permission.split('|').some((each) => granted.includes(each));
}
